import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: 0,
  boxShadow: 24,
  px: 4,
  pb: 4,
  pt: 4,
  outline: "none",
};

const accountTypeMap = {
  1: "Standard",
  2: "Model submitter",
  3: "Curator",
  4: "Administrator",
};

const tooltipContent = (
  <span style={{ fontFamily: "PT Sans, sans-serif", fontSize: "14px", lineHeight: "1.4" }}>
    Four account types exist:<br />
    <b>Standard</b> - This is the account for general users<br />
    <b>Model submitter</b> - This account allows users to submit and maintain their models on the portal<br />
    <b>Curator</b> - This account is especially for users who curate submitted models to check for validity and completeness before they are available for others on the portal<br />
    <b>Administrator</b> - This account is restricted and allows users to maintain the portal
  </span>
);

const ChangeAccountTypeModal = ({ open, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [selectedType, setSelectedType] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState("");
  const [showCloseIcon, setShowCloseIcon] = useState(true);

  // Preselect user’s account type on open
  useEffect(() => {
    if (open && user) {
      setSelectedType(accountTypeMap[user.accType]);
      setSubmitted(false);
      setMessage("");
      setShowCloseIcon(true);
    }
  }, [open, user]);

  const handleChange = (event) => {
    setSelectedType(event.target.value);
  };

  const handleSubmit = () => {
    const currentTypeNumber = Number(user.accType);
    const newTypeNumber = Object.keys(accountTypeMap).find(
      (key) => accountTypeMap[key] === selectedType
    );

    if (!newTypeNumber) return;

    if (Number(newTypeNumber) < currentTypeNumber) {
      setMessage("Your account has been updated");
    } else if (Number(newTypeNumber) > currentTypeNumber) {
      setMessage(
        "Your account type change has been requested. Once an administrator has reviewed your request you will be updated on their decision"
      );
    } else {
      setMessage("No changes were made");
    }

    setSubmitted(true);
    setShowCloseIcon(false);
  };

  const handleClose = () => {
    onClose();
    navigate("/account/2");
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" } }}
    >
      <Box sx={modalStyle}>
        {/* Close Button */}
        {showCloseIcon && (
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 10,
              padding: "4px",
              cursor: "pointer",
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}

        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: "12px",
            left: 0,
            width: "100%",
            textAlign: "center",
            fontFamily: "Poppins, sans-serif",
            fontSize: "32px",
            fontWeight: 400,
            color: "#142434",
          }}
        >
          Change Account Type
        </Typography>

        {/* Divider */}
        <Divider
          sx={{
            position: "absolute",
            top: "70px",
            left: 0,
            width: "100%",
            borderColor: "#489596",
          }}
        />

        {/* Content Area */}
        <Box mt={9}>
          {!submitted ? (
            <>
              {/* Intro Text */}
              <Typography
                sx={{
                  fontFamily: "PT Sans, sans-serif",
                  fontSize: "16px",
                  fontWeight: 400,
                  color: "#142434",
                  mb: 2,
                }}
              >
                All upgrades to account types need to be approved by an Administrator. Please request the account type you require{" "}
                <Tooltip title={tooltipContent} arrow>
                  <HelpOutlineIcon
                    sx={{
                      color: "#489596",
                      fontSize: 18,
                      verticalAlign: "middle",
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
                :
              </Typography>

              {/* Radio Buttons */}
              <RadioGroup value={selectedType} onChange={handleChange}>
                {Object.values(accountTypeMap).map((type) => (
                  <FormControlLabel
                    key={type}
                    value={type}
                    control={<Radio sx={{ color: "#489596", "&.Mui-checked": { color: "#489596" } }} />}
                    label={
                      <Typography
                        sx={{
                          fontFamily: "PT Sans, sans-serif",
                          fontSize: "16px",
                          color: "#142434",
                        }}
                      >
                        {type}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>

              {/* Change Button */}
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    width: "190px",
                    height: "42px",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#FFFFFF",
                    backgroundColor: "#489596",
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "#3a7b7f", boxShadow: "none" },
                    textTransform: "none",
                  }}
                >
                  Change
                </Button>
              </Box>
            </>
          ) : (
            <>
              {/* Message after submission */}
              <Typography
                sx={{
                  fontFamily: "PT Sans, sans-serif",
                  fontSize: "16px",
                  color: "#142434",
                  mb: 4,
                  textAlign: "center",
                }}
              >
                {message}
              </Typography>

              {/* Close Button */}
              <Box display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  onClick={handleClose}
                  sx={{
                    width: "190px",
                    height: "42px",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#FFFFFF",
                    backgroundColor: "#489596",
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "#3a7b7f", boxShadow: "none" },
                    textTransform: "none",
                  }}
                >
                  Close
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ChangeAccountTypeModal;
